import React from 'react';
import { approvalsOff } from '../../_utils/receptionMode';
import { pounds } from './bailey/format';
import { hapticTick } from '../../_utils/haptics';

// The words of the message being approved, when the server sends them
// (preview.message, or preview.body), trimmed to what fits on a card. The
// owner should see what goes out, not only who it goes to and what it costs.
export const messageOf = (preview) => {
    const p = preview || {};
    const raw = [p.message, p.body].find(v => typeof v === 'string' && v.trim());
    if (!raw) return '';
    const t = raw.trim().replace(/\s+/g, ' ');
    return t.length > 300 ? `${t.slice(0, 299).replace(/\s+\S*$/, '')}…` : t;
};

const REASONS = {
    'needs-approval': 'it still needs approving',
    'not-found': 'it couldn’t be found',
    forbidden: 'you don’t have access to that',
};

/**
 * What approving did. A 200 only means the approval was recorded: the pinned
 * action then runs and can still be refused (no credits, nothing to send).
 * The server answers { ok, ask, result } with result { ok, message,
 * refusedReason } (or an `outcome` of the same shape). Returns
 * { ok: true } or { ok: false, reason } in words an owner can read.
 */
export const readApproval = (res) => {
    const body = (res && res.data && (res.data.responseData || res.data)) || {};
    const result = body.result || body.outcome || null;
    // (A 200's own `message` is the wrapper's "Api Result", never a reason.)
    if (body.ok === false) return { ok: false, reason: REASONS[body.reason] || 'it couldn’t be approved' };
    if (!result || result.ok !== false) return { ok: true };
    const why = result.message || result.error || REASONS[result.refusedReason || result.reason] || result.refusedReason || result.reason;
    return { ok: false, reason: (typeof why === 'string' && why.trim()) || 'something stopped it' };
};

// The server's words when the approval itself was refused (expired, gone…)
export const approvalErrorOf = (err) => {
    const d = err && err.response && err.response.data;
    return (d && (d.message || (d.error && d.error.message))) || 'That couldn’t be approved. Try again.';
};

/**
 * Something that needs the owner's OK. The numbers get their own block,
 * before the buttons: the person is agreeing to a cost. Once answered the
 * buttons go and the card says what came of it, including when it didn't go.
 */
export default function ApprovalCard({ approval, busy, onApprove, onReject }) {
    const p = approval.preview || {};
    // The field is `cost`, in pounds. An owner thinks in pounds, not credits,
    // and credits alone make a real cost read like a game token.
    const money = p.cost != null ? pounds(p.cost) : null;
    const figs = [
        p.recipients != null && { k: 'People', v: p.recipients },
        money && { k: 'Cost', v: money },
        // Pounds only: an old preview with credits and no cost reads at 2p each.
        !money && p.credits != null && { k: 'Cost', v: pounds((Number(p.credits) || 0) * 0.02) }
    ].filter(Boolean);
    const sends = p.recipients != null;
    const words = messageOf(p);

    return (
        <div className={`agt-card bly-approve${approval.failed ? ' is-failed' : ''}`}>
            <p className="bly-eyebrow is-amber">Needs your OK</p>
            <h4 className="bly-approve-title">{p.label ? `Send “${p.label}”?` : 'This needs your OK'}</h4>
            {(approval.tellThem || !p.label) && <p className="agt-card-body">{approval.tellThem || 'This one needs approving first.'}</p>}
            {words && <blockquote className="bly-approve-message" aria-label="The message">{words}</blockquote>}
            {!!figs.length && (
                <div className="bly-figs">
                    {figs.map(e => (
                        <div key={e.k}><small>{e.k}</small><b>{e.v}</b></div>
                    ))}
                </div>
            )}
            {approval.failed ? (
                <p className="bly-status is-error" role="alert"><i aria-hidden="true" />It didn’t go: {approval.failed}</p>
            ) : approval.approved ? (
                <p className="bly-status" aria-live="polite"><i aria-hidden="true" />Approved</p>
            ) : approvalsOff() ? (
                <p className="bly-status" aria-live="polite"><i aria-hidden="true" />Waiting for the owner to approve</p>
            ) : (
                <div className="bly-actions">
                    <button type="button" className="bly-btn is-ghost" disabled={busy} onClick={() => { hapticTick(); onReject(approval.approvalId); }}>
                        Not now
                    </button>
                    <button type="button" className="bly-btn is-primary" disabled={busy} onClick={() => { hapticTick(); onApprove(approval.approvalId); }}>
                        {busy ? 'Approving…' : sends ? 'Approve and send' : 'Approve'}
                    </button>
                </div>
            )}
        </div>
    );
}
